import { apiFetch } from "api/auth";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

/** Single follow-up row from the list API (one row per lead) */
export interface FollowUpListItem {
  id: string;
  leadNo: string;
  clientName: string;
  email: string | null;
  phone: string | null;
  status: string;
  stage: string | null;
  markName: string | null;
  createdAt: string;
}

/** Paginated response from list follow-ups API */
export interface ListFollowUpsResponse {
  items: FollowUpListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ApiSuccessResponse<T> {
  success: true;
  message: string;
  data: T;
}

interface ApiErrorResponse {
  success?: false;
  message?: string | string[];
  error?: string;
  statusCode?: number;
}

function getApiErrorMessage(json: ApiErrorResponse | undefined, fallback: string): string {
  if (!json) return fallback;
  if (Array.isArray(json.message)) {
    return json.message.map(String).filter(Boolean).join(" ");
  }
  if (typeof json.message === "string" && json.message.trim()) {
    return json.message;
  }
  if (typeof json.error === "string" && json.error.trim()) {
    return json.error;
  }
  return fallback;
}

export type FollowUpStageFilter = "DISCUSSION" | "FILING" | "STATUS";
export type FollowUpSort = "newest" | "oldest";

export interface ListFollowUpsParams {
  page?: number;
  limit?: number;
  search?: string;
  stage?: FollowUpStageFilter;
  sort?: FollowUpSort;
}

export async function listFollowUps(
  pageOrParams: number | ListFollowUpsParams = 1,
  limit = 14,
  search?: string
): Promise<ListFollowUpsResponse> {
  const opts: ListFollowUpsParams =
    typeof pageOrParams === "number"
      ? { page: pageOrParams, limit, search }
      : pageOrParams;

  const params = new URLSearchParams({
    page: String(opts.page ?? 1),
    limit: String(opts.limit ?? 14),
  });
  if (opts.search?.trim()) params.set("search", opts.search.trim());
  if (opts.stage) params.set("stage", opts.stage);
  if (opts.sort) params.set("sort", opts.sort);

  const res = await apiFetch(`${API_BASE}/follow-up/leads?${params}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const json = (await res.json()) as ApiSuccessResponse<ListFollowUpsResponse> | ApiErrorResponse;

  if (!res.ok) {
    throw new Error(getApiErrorMessage(json as ApiErrorResponse, "Failed to load follow-ups"));
  }

  return (json as ApiSuccessResponse<ListFollowUpsResponse>).data;
}
